import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { subscriptionAPI } from '../../api';
import { Sidebar } from '../dashboard_component';

function SubscriptionUsers() {
  const [users, setUsers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const navigate = useNavigate();

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch subscription users
      const usersResponse = await subscriptionAPI.getSubscriptionUsers();
      console.log('Users response:', usersResponse);
      
      if (usersResponse.data) {
        const responseData = usersResponse.data;
        // Handle both possible response structures
        if (responseData.users) {
          setUsers(responseData.users || []);
          setPlanInfo(responseData.plan_info || {});
        } else if (Array.isArray(responseData)) {
          setUsers(responseData || []);
        } else {
          setUsers([]);
        }
      } else {
        const errorMessage = usersResponse.error || usersResponse.detail || 'Failed to load subscription users';
        toast.error(errorMessage);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to load subscription data';
      toast.error(errorMessage);
      setUsers([]);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await subscriptionAPI.addSubscriptionUser(newUserEmail);
      if (response.message || response.success) {
        const successMessage = response.message || `Invitation sent to ${newUserEmail}`;
        toast.success(successMessage);
        setNewUserEmail('');
        fetchData(); // Refresh the list
      } else {
        const errorMessage = response.error || response.detail || 'Failed to add user';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to add user';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateToken = async () => {
    setIsLoading(true);
    try {
      const response = await subscriptionAPI.generateToken();
      console.log('Generate token response:', response);
      
      if (response.token || response.data?.token) {
        const tokenData = response.data || response;
        toast.success('Token generated successfully');
        
        // Add the new token to the list with an ID
        const newToken = {
          id: Date.now(), // Temporary ID since backend doesn't return one
          token: tokenData.token,
          expires_at: tokenData.expires_at,
          share_link: tokenData.share_link || `${window.location.origin}/join-subscription/${tokenData.token}`
        };
        
        setTokens(prev => [...prev, newToken]);
      } else {
        const errorMessage = response.error || response.detail || 'Failed to generate token';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error generating token:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to generate token';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };


  const handleRevokeUser = async (userId) => {
  if (!window.confirm('Are you sure you want to remove this user from your subscription?')) return;

  try {
    // Call the backend API to remove the user
    const response = await subscriptionAPI.removeSubscriptionUser(userId);
    
    if (response.data) {
      toast.success(response.message);
      // Refresh the user list
      fetchData();
    } else {
      toast.error(response.data.message);
      fetchData();
      
    }
  } catch (error) {
    console.error('Error removing user:', error);
    const errorMessage = error.response?.data?.detail || 
                        error.data?.detail || 
                        error.message || 
                        'Failed to remove user';
    toast.error(errorMessage);
  }
};


  // const handleRevokeUser = async (userId) => {
  //   if (!window.confirm('Are you sure you want to remove this user from your subscription?')) return;

  //   try {
  //     // Note: Backend doesn't have this endpoint yet, so we'll just remove from frontend
  //     // You should implement this endpoint in backend
  //     setUsers(prev => prev.filter(user => user.id !== userId));
  //     toast.success('User removed from subscription');
  //   } catch (error) {
  //     console.error('Error removing user:', error);
  //     const errorMessage = error.response?.data?.detail || 
  //                         error.data?.detail || 
  //                         error.message || 
  //                         'Failed to remove user';
  //     toast.error(errorMessage);
  //   }
  // };

  const handleRevokeToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to revoke this token?')) return;

    try {
      // Note: Backend doesn't have this endpoint yet, so we'll just remove from frontend
      // You should implement this endpoint in backend
      setTokens(prev => prev.filter(token => token.id !== tokenId));
      toast.success('Token revoked');
    } catch (error) {
      console.error('Error revoking token:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to revoke token';
      toast.error(errorMessage);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.info(`${label} copied to clipboard`);
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div 
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <ToastContainer />
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent mb-3">
              Subscription Users Management
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl">
              Manage users who share your subscription plan
            </p>
            {planInfo && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-700">
                  <span className="font-semibold">Plan:</span> {planInfo.category || 'Normal'} | 
                  <span className="font-semibold ml-3">Max Users:</span> {planInfo.max_users || 1} | 
                  <span className="font-semibold ml-3">Current Users:</span> {users.length + 1}
                </p>
              </div>
            )}
          </div>

          {/* Add User Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Add User to Subscription</h2>
            <form onSubmit={handleAddUser} className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="Enter user's email address"
                className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <button
                type="submit"
                disabled={isLoading}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 transition-all duration-300"
              >
                {isLoading ? 'Sending...' : 'Send Invitation'}
              </button>
            </form>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'users' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('users')}
            >
              Users ({users.length})
            </button>
            {/* <button
              className={`px-4 py-2 font-medium ${activeTab === 'tokens' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('tokens')}
            >
              Share Tokens ({tokens.length})
            </button> */}
          </div>

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                          No users added yet. Invite users by email above.
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {user.email}
                              </div>
                              {user.sub_user_id && (
                                <div className="text-sm text-gray-500">User ID: {user.sub_user_id}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              user.status === 'active' 
                                ? 'bg-green-100 text-green-800'
                                : user.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {user.status?.charAt(0).toUpperCase() + user.status?.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {user.joined_at ? new Date(user.joined_at).toLocaleDateString() : 'Not joined yet'}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleRevokeUser(user.email)}
                              className="text-red-600 hover:text-red-900 font-medium text-sm"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tokens Tab */}
          {activeTab === 'tokens' && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-800">Shareable Tokens</h3>
                <button
                  onClick={handleGenerateToken}
                  disabled={isLoading}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300"
                >
                  Generate New Token
                </button>
              </div>
              
              <div className="space-y-4">
                {tokens.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No active tokens. Generate one to share with users.
                  </p>
                ) : (
                  tokens.map((token) => (
                    <div key={token.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono break-all">
                              {token.token}
                            </code>
                            <div className="flex gap-2">
                              <button
                                onClick={() => copyToClipboard(token.token, 'Token')}
                                className="text-blue-600 hover:text-blue-800 text-sm whitespace-nowrap"
                              >
                                Copy Token
                              </button>
                              <button
                                onClick={() => copyToClipboard(token.share_link, 'Share Link')}
                                className="text-green-600 hover:text-green-800 text-sm whitespace-nowrap"
                              >
                                Copy Link
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">Expires:</span> {token.expires_at ? new Date(token.expires_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRevokeToken(token.id)}
                          className="text-red-600 hover:text-red-800 font-medium ml-4"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-blue-800 mb-2">How it works:</h3>
            <ul className="list-disc list-inside text-blue-700 space-y-1">
              <li>Users can join your subscription via email invitation or token</li>
              <li>Each user counts towards your subscription limit</li>
              <li>You can remove users anytime to free up slots</li>
              <li>Tokens expire after 7 days and can be revoked anytime</li>
              <li>Shared users get access to all premium features</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="mt-8">
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/2348113315252"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3.5 px-6 rounded-xl font-semibold transition-all duration-300 flex-1 sm:flex-none"
              >
                <i className="fab fa-whatsapp text-lg"></i>
                <span>Chat on WhatsApp</span>
              </a>

              <button
                onClick={() => navigate('/cbt_creator/dashboard')}
                className="group flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3.5 px-6 rounded-xl font-semibold transition-all duration-300 flex-1 sm:flex-none"
              >
                <i className="fas fa-arrow-left text-lg"></i>
                <span>Back to Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionUsers;